import React, { CSSProperties } from 'react'
import { Fragment, ReactNode, useMemo, useState } from 'react'
import { Col, Dropdown, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'

import { ChevronDown, ChevronRight, Minus, RefreshCw } from 'react-feather'
import * as Paging from '../pagination/pagination'
import * as PagingUtils from '../pagination/paginationUtils'
import * as Sort from './sortFilter'
import * as SortUtils from './sortUtils'

import { Loading } from '../loading/loading'
import itemRender from './itemRender'

import { formatIncomingDateTime } from '../../../utilities/dateFormat'
import { FormSelect, SelectOption } from '../form/Select'

interface FilterDropdownOption {
	value: string
	label: string
}

interface SectionStyles {
	thead?: React.CSSProperties
	trDepth0?: React.CSSProperties
	trDepth1?: React.CSSProperties
	trDepth2?: React.CSSProperties
}

interface ListingAction {
	name: string
	method: (selected: string[]) => Promise<void>
	isPending?: boolean
}

interface RowAction {
	name: string
	method: (selected: string) => Promise<void>
	disabled?: boolean
}

type TChildren<T> = T & { child?: React.ReactNode; children?: T[] }
interface ChildItem {
	getIDFunc: () => string
}

interface ListingColumn<T> {
	// body stuff
	value: (item: T) => string
	render: (item: T) => React.ReactNode
	colSpan?: (item: T) => number
	hidden?: (item: T) => boolean
	customSortCompare?: (a: string, b: string) => number
	// header stuff
	showHeader: boolean
	headerText?: string
	headerClass?: string
	sortJustify?: 'start' | 'end'
	sortColumnName?: keyof T | string
	filterType?: 'string' | 'dropdown' | 'dateRange' | 'multiselect'
	filterOptions?: {
		columnName: keyof T | string
		options?: FilterDropdownOption[]
	}
	width?: string
}

interface ListingProps<T> {
	name: string
	namePlural: string
	allowCsvExport?: boolean
	getIDFunc: (type: T) => string // (t: T) => t.myID
	selectedActions?: ListingAction[]
	individualActions?: (item: T) => RowAction[]
	list: (T & { children?: TChildren<T>[] | ChildItem[] })[]
	columns: ListingColumn<T>[]
	defaultSort: Sort.SortFilter<T>
	isLoading: boolean
	paginationControls: 'top' | 'bottom' | 'both' | 'none'
	customButtons?: ReactNode[]
	customFilters?: ReactNode[]
	defaultSearchFilter?: SearchFilter<T>
	externalSearchFilters?: SearchFilter<T>
	listingBodyOnly?: boolean
	sectionStyles?: SectionStyles
	borderRadius?: boolean
	boxShadow?: boolean
	smallPagination?: boolean
	autoExpand?: boolean
	initialExpanded?: Record<string, boolean>
	headerPadRow?: boolean
	hideDisplayCount?: boolean
	handleGetMore?: () => void
	handleHotRefresh?: () => Promise<void> // Get new data without reloading page - prevents losing filters, sort and position
	responsive?: boolean
	pageFilter?: Paging.FiltersState // Allows parent to control pagination
	setPageFilter?: React.Dispatch<React.SetStateAction<Paging.FiltersState>>
	customPageSize?: number
	pageSizeIsFixed?: boolean
	hideTableHeader?: boolean
	innerTableClassName?: string
	customChildRender?: (item: ChildItem) => JSX.Element
}

type SearchFilter<T> = Record<keyof Partial<T> | string, string>

const Listing = <T,>({
	name,
	namePlural,
	allowCsvExport,
	getIDFunc,
	selectedActions,
	individualActions,
	list,
	columns,
	defaultSort,
	isLoading,
	paginationControls,
	customButtons,
	customFilters,
	defaultSearchFilter,
	externalSearchFilters,
	listingBodyOnly,
	sectionStyles,
	borderRadius,
	boxShadow,
	smallPagination,
	autoExpand,
	initialExpanded,
	headerPadRow,
	hideDisplayCount,
	handleGetMore,
	handleHotRefresh,
	responsive,
	pageFilter,
	setPageFilter,
	customPageSize,
	pageSizeIsFixed,
	hideTableHeader,
	innerTableClassName,
	customChildRender,
}: ListingProps<T>) => {
	const [defaultPageFilter, setDefaultPageFilter] = useState<Paging.FiltersState>({ page: 1, pageSize: customPageSize || PagingUtils.getPagingPreferences() })
	const [internalSearchFilters, setInternalSearchFilters] = useState<SearchFilter<T>>(defaultSearchFilter || ({} as SearchFilter<T>))
	const [sortFilters, setSortFilters] = useState<Sort.SortFilter<T>>(defaultSort)
	const [selectFilter, setSelectFilter] = useState<string[]>([])
	const [expandedChildren, setExpandedChildren] = useState<Record<string, boolean>>(initialExpanded || {})
	const [localLoading, setLocalLoading] = useState<boolean>(false)

	const [internalPageFilter, setInternalPageFilter] = pageFilter && setPageFilter ? [pageFilter, setPageFilter] : [defaultPageFilter, setDefaultPageFilter]
	const searchFilters = externalSearchFilters || internalSearchFilters

	const internalHandleHotRefresh = async () => {
		if (handleHotRefresh) {
			setLocalLoading(true)
			await handleHotRefresh()
			setLocalLoading(false)
		}
	}

	const searchFilterFunction = (record: T) =>
		columns.every((column) => {
			if (column.filterOptions) {
				if (column.filterType === 'dateRange') {
					const low = searchFilters[String(column.filterOptions.columnName) + '-low']
					const high = searchFilters[String(column.filterOptions.columnName) + '-high']
					// convert date to local time so it makes sense with filters
					const value = column.value(record) ? formatIncomingDateTime({ dateTime: column.value(record), format: 'FileDate' }) : column.value(record)
					if (low === undefined && high === undefined) {
						return true
					} else if (low && high) {
						return value >= low && value <= high
					} else if (low) {
						return value >= low
					} else if (high) {
						return value <= high
					}
					return true
				} else {
					const filterText = searchFilters[column.filterOptions.columnName]
					const value = column.value(record)
					if (filterText === 'All' && (column.filterType === 'dropdown' || column.filterType === 'multiselect')) {
						return true
					} else if (filterText && value && column.filterType === 'dropdown') {
						return value.toLowerCase() === filterText.toLowerCase()
					} else if (filterText && value) {
						return value.toLowerCase().includes(filterText.toLowerCase())
					} else if (filterText && !value) {
						return false
					}
				}
			}
			return true
		})
	const pageFilterFunction = (_record: T, index: number) => PagingUtils.pageFilter(internalPageFilter, index)
	const sortFunction = SortUtils.getSortFunction(sortFilters, columns)

	const unFilteredRecords = list
	const filteredRecords = unFilteredRecords.filter(searchFilterFunction)
	const sortedFilteredRecords = filteredRecords.sort(sortFunction)
	const paginatedFilteredSortRecords = paginationControls === 'none' ? sortedFilteredRecords : sortedFilteredRecords.filter(pageFilterFunction)

	const hasSelectedActions = !!(selectedActions && selectedActions.length > 0)
	const hasIndividualActions = individualActions && individualActions.length > 0
	const hasChildren = list.some((row) => row.children)
	const colSpanFullWidth = columns.length + (hasSelectedActions || allowCsvExport ? 1 : 0) // don't add the row for has children because it gets indented

	const PageSizeDropdown = () => (
		<div className="d-flex flex-row align-items-center">
			<span className="span-bold align-items-center">Show</span>
			<Dropdown className="small-pagination">
				<Dropdown.Toggle variant="light" id="dropdown-action">
					{internalPageFilter.pageSize} records per page
				</Dropdown.Toggle>
				<Dropdown.Menu>
					{[10, 25, 50, 100].map((pageSize) => (
						<Dropdown.Item
							key={pageSize}
							onClick={() => {
								setInternalPageFilter({ page: 1, pageSize })
								PagingUtils.storePagingPreferences(pageSize)
							}}
						>
							{pageSize} records per page
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>
		</div>
	)

	const PaginationControls = () => (
		<>
			<Col sm="auto">
				{smallPagination ? (
					<Paging.PaginationSmall
						filters={internalPageFilter}
						setFilterDispatch={setInternalPageFilter}
						allPaginatedRecordsLength={filteredRecords.length}
						onLoadMoreClick={handleGetMore}
					/>
				) : (
					<Paging.Pagination
						filters={internalPageFilter}
						setFilterDispatch={setInternalPageFilter}
						allPaginatedRecordsLength={filteredRecords.length}
						onLoadMoreClick={handleGetMore}
					/>
				)}
			</Col>
			<Col sm="auto">{!pageSizeIsFixed && <PageSizeDropdown />}</Col>
		</>
	)

	const ListingTable = useMemo(() => {
		const setSelect = (id: string, checked: boolean) => {
			if (checked && id === 'All') {
				setSelectFilter(filteredRecords.map(getIDFunc))
			} else if (checked) {
				setSelectFilter([...selectFilter, id])
			} else if (id === 'All') {
				setSelectFilter([])
			} else {
				setSelectFilter(selectFilter.filter((t) => t !== id))
			}
		}

		const ExpandedControl = ({ itemId, hasChild, depth }: { itemId: string; hasChild: boolean; depth: number }) => {
			const toggleExpanded = () => {
				setExpandedChildren({ ...expandedChildren, [itemId]: !expandedChildren[itemId] })
			}

			if (!hasChildren) {
				return null
			}

			return (
				<>
					{depth > 0 && (hasSelectedActions || allowCsvExport) && <td colSpan={depth}></td>}
					<td>
						<div>
							{hasChild ? (
								expandedChildren[itemId] ? (
									<ChevronDown size={20} onClick={toggleExpanded} />
								) : (
									<ChevronRight size={20} onClick={toggleExpanded} />
								)
							) : (
								<Minus size={24} color={'#fff'} />
							)}
						</div>
					</td>
				</>
			)
		}

		// Does not support custom children
		const ExpandAllControl = ({
			items,
			itemIdFunc,
		}: {
			items: (T & { children?: (T & { child?: React.ReactNode; children?: T[] })[] })[]
			itemIdFunc: (type: T) => string
		}) => {
			const open = Object.values(expandedChildren).some((e) => e == true) // Open if any rows are expanded

			const toggleExpanded = (set: boolean) => {
				// Shortcut to remove all open when minimising all
				if (!set) {
					setExpandedChildren({})
					return
				}

				// Get all the children ids and set as true
				const ids: Record<string, boolean> = {}
				items.forEach((i) => {
					ids[itemIdFunc(i)] = set
					if (i.children) {
						i.children.forEach((c) => {
							ids[itemIdFunc(c)] = set
							if (c.children) {
								c.children.forEach((cc) => {
									ids[itemIdFunc(cc)] = set
								})
							}
						})
					}
				})
				setExpandedChildren(ids)
			}

			return (
				<OverlayTrigger placement="right" overlay={<Tooltip id="expand-toggle-tooltip">{open ? 'Minimise All' : 'Expand All'}</Tooltip>}>
					{open ? <ChevronDown size={20} onClick={() => toggleExpanded(false)} /> : <ChevronRight size={20} onClick={() => toggleExpanded(true)} />}
				</OverlayTrigger>
			)
		}

		return (
			<Table
				responsive={responsive}
				className={`mb-0 table-listing ${boxShadow ? 'box-shadow' : ''} ${borderRadius ? 'border-radius' : ''} ${innerTableClassName}`}
			>
				{!hideTableHeader && (
					<thead style={sectionStyles?.thead}>
						<tr>
							{hasChildren && !autoExpand && (
								<th style={styles.expandAll}>
									<ExpandAllControl items={list as (T & { children: TChildren<T>[] })[]} itemIdFunc={getIDFunc} />
								</th>
							)}
							{(hasSelectedActions || allowCsvExport) && (
								<th style={styles.selectHeader}>
									<Row>
										<Col className="center-flex">
											<Form.Control
												style={styles.selectedActions}
												onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
													setSelect(event.target.value, event.target.checked)
												}}
												type="checkbox"
												id={'select_' + name}
												name={'select_' + name}
												checked={selectFilter.length > 0}
												value={'All'}
											/>
										</Col>
									</Row>
								</th>
							)}
							{columns.map((column, index) => (
								<th key={index} style={{ width: column.width || 'inherit' }} className={column.headerClass}>
									{column.showHeader && column.headerText && column.sortColumnName && (
										<>
											<Row>
												<Col className="upper-table-header">
													<Sort.SortButton
														sort={sortFilters}
														setSort={setSortFilters}
														columnName={column.sortColumnName}
														preIconContent={<Col>{column.headerText}</Col>}
														sortJustify={column.sortJustify}
													/>
												</Col>
											</Row>
											<Row>
												<Col>
													{column.filterType && column.filterOptions && column.filterType === 'string' && (
														<Form.Control
															onChange={(event) => {
																setInternalSearchFilters({
																	...searchFilters,
																	[column.filterOptions!.columnName]: event.target.value,
																})
															}}
															type="text"
															id={`search_${String(column.filterOptions.columnName)}`}
															name={`search_${String(column.filterOptions.columnName)}`}
															value={searchFilters[column.filterOptions.columnName] || ''}
															placeholder="Type to filter"
														/>
													)}
													{column.filterType &&
														column.filterOptions &&
														(column.filterType === 'dropdown' || column.filterType === 'multiselect') && (
															<FormSelect
																name={`column-filter-${index}`}
																options={[{ value: 'All', label: 'All' }].concat(column.filterOptions.options || [])}
																value={
																	column.filterOptions?.options?.find(
																		(option) => option.value === searchFilters[column.filterOptions!.columnName]
																	)?.value || 'All'
																}
																onChange={(val) =>
																	val &&
																	setInternalSearchFilters({
																		...searchFilters,
																		[column.filterOptions!.columnName]: (val as SelectOption<string>).value,
																	})
																}
																containerClassName={'mb-0 span-normal'}
																disableCustomOption
															/>
														)}
													{column.filterType && column.filterOptions && column.filterType === 'dateRange' && (
														<>
															<Form.Control
																onChange={(event) => {
																	setInternalSearchFilters({
																		...searchFilters,
																		[String(column.filterOptions!.columnName) + '-low']: event.target.value,
																	})
																}}
																type="date"
																max={searchFilters[String(column.filterOptions.columnName) + '-high'] || ''}
																id={`search_${String(column.filterOptions.columnName)}`}
																name={`search_${String(column.filterOptions.columnName)}`}
																value={searchFilters[String(column.filterOptions.columnName) + '-low'] || ''}
																style={styles.dateRangeControl}
															/>
															<Form.Control
																onChange={(event) => {
																	setInternalSearchFilters({
																		...searchFilters,
																		[String(column.filterOptions!.columnName) + '-high']: event.target.value,
																	})
																}}
																type="date"
																min={searchFilters[String(column.filterOptions.columnName) + '-low'] || ''}
																id={`search_${String(column.filterOptions.columnName)}`}
																name={`search_${String(column.filterOptions.columnName)}`}
																value={searchFilters[String(column.filterOptions.columnName) + '-high'] || ''}
																style={styles.dateRangeControl}
															/>
														</>
													)}
												</Col>
											</Row>
										</>
									)}
								</th>
							))}
						</tr>
					</thead>
				)}

				<tbody>
					{isLoading && (
						<tr>
							<td colSpan={colSpanFullWidth}>
								<div className="center-flex p-5">
									<Loading show />
								</div>
							</td>
						</tr>
					)}
					{!isLoading && (
						<>
							{headerPadRow && (
								<tr className="header-pad-row">
									<td colSpan={colSpanFullWidth}></td>
								</tr>
							)}
							{paginatedFilteredSortRecords.map((item) => (
								<Fragment key={getIDFunc(item)}>
									<tr
										className={`grouped-row ${expandedChildren[getIDFunc(item)] || autoExpand ? 'expanded-listing' : ''}`}
										style={sectionStyles?.trDepth0}
									>
										{!autoExpand && <ExpandedControl hasChild={!!item.children} itemId={getIDFunc(item)} depth={0} />}
										{itemRender(item, {
											hasSelectedActions,
											hasIndividualActions,
											individualActions,
											allowCsvExport,
											columns,
											getIDFunc,
											selectFilter,
											setSelectFilter,
											setSelect,
										})}
									</tr>
									{item.children &&
										(expandedChildren[getIDFunc(item)] === true || autoExpand) &&
										!customChildRender &&
										(item.children as TChildren<T>[]).sort(sortFunction).map((childItem) => (
											<Fragment key={`${getIDFunc(item)}-${getIDFunc(childItem)}`}>
												<tr className="child-listing" style={sectionStyles?.trDepth1}>
													{!autoExpand && (
														<ExpandedControl
															hasChild={!!childItem.child || !!childItem.children}
															itemId={getIDFunc(childItem)}
															depth={1}
														/>
													)}
													{itemRender(childItem, {
														hasSelectedActions,
														hasIndividualActions: false,
														individualActions: undefined,
														allowCsvExport: false,
														columns,
														getIDFunc,
														selectFilter,
														setSelectFilter,
														setSelect,
													})}
												</tr>
												{childItem.child && (expandedChildren[getIDFunc(childItem)] === true || autoExpand) && (
													<tr style={sectionStyles?.trDepth2}>
														<td colSpan={1}></td>
														<td colSpan={colSpanFullWidth} className="p-0">
															{childItem.child}
														</td>
													</tr>
												)}
												{childItem.children &&
													(expandedChildren[getIDFunc(childItem)] === true || autoExpand) &&
													childItem.children.sort(sortFunction).map((grandChildItem) => (
														<tr
															key={`${getIDFunc(item)}-${getIDFunc(childItem)}-${getIDFunc(grandChildItem)}`}
															className="grandchild-listing"
															style={sectionStyles?.trDepth2}
														>
															{!autoExpand && <td></td>}
															{itemRender(grandChildItem, {
																hasSelectedActions,
																hasIndividualActions: false,
																individualActions: undefined,
																allowCsvExport: false,
																columns: columns,
																getIDFunc: getIDFunc,
																selectFilter,
																setSelectFilter,
																setSelect,
															})}
														</tr>
													))}
											</Fragment>
										))}
									{item.children &&
										(expandedChildren[getIDFunc(item)] === true || autoExpand) &&
										customChildRender &&
										item.children.map((childItem) => (
											<Fragment key={`${getIDFunc(item)}-${(childItem as ChildItem).getIDFunc()}`}>
												{customChildRender?.(childItem as ChildItem)}
											</Fragment>
										))}
								</Fragment>
							))}
							{paginatedFilteredSortRecords.length === 0 && (
								<tr>
									<td colSpan={colSpanFullWidth}>
										<div className="center-flex p-5">No {namePlural} found</div>
									</td>
								</tr>
							)}
						</>
					)}
				</tbody>
			</Table>
		)
	}, [
		responsive,
		boxShadow,
		borderRadius,
		sectionStyles?.thead,
		sectionStyles?.trDepth0,
		sectionStyles?.trDepth1,
		sectionStyles?.trDepth2,
		hasChildren,
		autoExpand,
		list,
		getIDFunc,
		hasSelectedActions,
		allowCsvExport,
		name,
		selectFilter,
		columns,
		isLoading,
		colSpanFullWidth,
		headerPadRow,
		paginatedFilteredSortRecords,
		namePlural,
		filteredRecords,
		expandedChildren,
		sortFilters,
		searchFilters,
		sortFunction,
		hideTableHeader,
	])

	if (listingBodyOnly) {
		return ListingTable
	}

	const hideHeaderRow = !(
		customButtons ||
		hasSelectedActions ||
		allowCsvExport ||
		handleHotRefresh ||
		paginationControls === 'top' ||
		paginationControls === 'both'
	)

	return (
		<>
			{!hideHeaderRow && (
				<Row className="align-items-center py-3">
					{customButtons?.map((button, index) => (
						<Col xs="auto" key={index}>
							{button}
						</Col>
					))}
					{(hasSelectedActions || allowCsvExport) && (
						<Col sm={'auto'}>
							<Dropdown>
								<Dropdown.Toggle variant="light" style={styles.selectedActionsDropdown} id="dropdown-action">
									Bulk Actions
								</Dropdown.Toggle>
								<Dropdown.Menu>
									{hasSelectedActions &&
										selectedActions!.map((action, i) => (
											<Dropdown.Item
												key={i}
												onClick={async () => {
													await action.method(selectFilter)
													setSelectFilter([])
												}}
												disabled={action.isPending}
											>
												{action.isPending && <Loading show small />}
												{action.name}
											</Dropdown.Item>
										))}
								</Dropdown.Menu>
							</Dropdown>
						</Col>
					)}
					{(hasSelectedActions || allowCsvExport) && (
						<Col sm={'auto'}>
							<span className="span-grey">
								{selectFilter.length /* || paginatedFilteredSortRecords.length */} {list.length === 1 ? name : namePlural} selected
							</span>
						</Col>
					)}
					<Col />
					{handleHotRefresh && (
						<Col sm={'auto'}>
							<OverlayTrigger
								placement="top"
								overlay={<Tooltip id="expand-toggle-tooltip">{localLoading ? 'Refreshing...' : 'Refresh'}</Tooltip>}
							>
								<RefreshCw
									key="refresh"
									onClick={internalHandleHotRefresh}
									style={{ animation: localLoading ? 'spin-anim 3s linear infinite' : undefined }}
								/>
							</OverlayTrigger>
						</Col>
					)}
					{(paginationControls === 'top' || paginationControls === 'both') && <PaginationControls />}
				</Row>
			)}

			{customFilters && customFilters.length && (
				<Row className="align-items-center py-3">
					{customFilters.map((filter, index) => (
						<Col xs="auto" key={index}>
							{filter}
						</Col>
					))}
				</Row>
			)}

			<Row className={`${hideHeaderRow ? 'pt-2' : ''}`}>
				<Col className="table-wrapper">
					{ListingTable}
					<Row className="py-3 align-items-center justify-content-between">
						<Col sm="auto" className="d-flex">
							{!hideDisplayCount && (
								<span className="span-body-2">
									{unFilteredRecords.length ? (
										<>
											Displaying{' '}
											<span className="span-blue">
												{PagingUtils.calculatePageStart(internalPageFilter)}
												{' to '}
												{PagingUtils.calculatePageTo(internalPageFilter, filteredRecords.length)}
											</span>{' '}
											of {filteredRecords.length} {namePlural}
											{unFilteredRecords.length !== filteredRecords.length && (
												<>
													<span className="span-bold">{' shown '}</span>
													{`(${unFilteredRecords.length} ${namePlural} total)`}
												</>
											)}
										</>
									) : (
										<>No {namePlural} found</>
									)}
								</span>
							)}
						</Col>
						{(paginationControls === 'bottom' || paginationControls === 'both') && <PaginationControls />}
					</Row>
				</Col>
			</Row>
		</>
	)
}

const styles = {
	selectHeader: {
		width: '80px',
	},
	expandAll: {
		width: '45px',
	},
	selectedActions: {
		boxShadow: 'none',
		width: '16px',
		height: '16px',
		appearance: 'revert',
	} as CSSProperties,
	dateRangeColumn: {
		display: 'flex',
		justifyContent: 'space-around',
	},
	dateRangeControl: {
		width: '46%',
		display: 'inline-block',
		marginLeft: '2px',
		marginRight: '2px',
	},
	selectedActionsDropdown: {
		display: 'flex',
		alignItems: 'center',
	},
}

export { Listing }
export type { FilterDropdownOption, ListingAction, RowAction, ChildItem, ListingColumn, SearchFilter, SectionStyles }
